import { createStyles, Group, MantineProvider, Menu } from "@mantine/core";
import { rpaTheme } from "../../theme/rpaTheme";
import { OrdersTableFooter, OrdersTableV2 } from "./OrdersTableV2";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { PaginationV2 } from "../../components/PaginationV2";
import React from "react";
import { useRpiPagination } from "../../hooks/usePagination";
import { useAuthUser } from "../../hooks/useAuthUser";
import { Button, tokens } from "@blurb/codex-react";
import { IconV3, useToasts } from "@rpi/openapi-core";
import { AddPaymentMethod } from "../../components/Alerts/AddPaymentMethodAlert";
import { SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { orderSearchLabels, orderSearchRenderers } from "../../utils/searchField.util";
import { Heading } from "../../components/Typography";
import { PayNowDrawer } from "./PayNowDrawer";
import { PayNowButton, usePayNowState } from "./PayNowButton";
import { OrdersFilter, path as filterPath } from "./OrdersFilter";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";
import { EqualsSearchInput, SearchInput } from "../../configuration/filters/searchInput";

const useStyles = createStyles({
  container: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column",
    color: tokens.color.semantic.text.bold,
    gap: tokens.spacing[6],
  },
  row: { display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
});

export const OrdersViewV2 = () => {
  const user = useAuthUser();
  const pagination = useRpiPagination({ perPage: 20 });
  const { classes } = useStyles();
  const filterDrawerRoute = useDrawerRoute(filterPath);

  // Table Filters
  const [sortInput, setSortInput] = React.useState<GraphQL.SortInput | undefined>({
    by: GraphQL.OrderSearchField.CreatedAt,
    sortDirection: GraphQL.SortDirection.Desc,
  });

  const [generalSearch, setGeneralSearch] = React.useState<string | undefined>();
  const [searchFilters, setSearchFilters] = React.useState<SearchInput<GraphQL.OrderSearchField>[]>([]);

  const sandboxFilter: EqualsSearchInput<GraphQL.OrderSearchField> = {
    condition: GraphQL.SearchCondition.Equals,
    field: GraphQL.OrderSearchField.IsSandbox,
    firstValue: user.isSandbox.toString(),
  };

  const handleSearchOrdersSubmit = (searchValue: string) => {
    setGeneralSearch(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error } = GraphQL.useOrdersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: [sandboxFilter, ...searchFilters],
        generalInput: generalSearch, // generalInput searches by customerOrderId
        sort: sortInput,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.orders.totalCount),
    }
  );
  const orders = data?.orders.data;

  const { showPayNow, hasNoPaymentMethod, payNowOpen, openPayNow, closePayNow } = usePayNowState();
  const isCustomerOrUser = user.is.CUSTOMER_ADMIN || user.is.USER;

  const { showSuccessToast, showErrorToast } = useToasts();

  const { mutate: sendSandboxPaymentNotification, isLoading: isSandboxPaymentLoading } =
    GraphQL.useSandboxPaymentNotificationMutation({
      onSuccess: (data) => {
        if (data.sandboxPaymentNotification.success) {
          showSuccessToast(
            "Payment success webhook sent! Please check your endpoint and inbox for the confirmation email."
          );
        } else {
          // success denotes whether or not a success event was sent and indicates a successful submission either way
          showSuccessToast("Payment failed webhook sent! Please check your endpoint.");
        }
      },
      onError: (error) => {
        if (isGraphQLError(error)) {
          showErrorToast(
            'Payment webhook test failed. Make sure you have orders in "Awaiting Payment" status and try again.'
          );
        } else {
          showErrorToast("Sandbox payment notification failed.");
        }
      },
    });

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="orders-view" aria-label="Orders view">
        <Heading size="6xl">Orders</Heading>
        <AddPaymentMethod />
        <div className={classes.row}>
          <SearchFilterBar
            placeholder="Search by Order ID"
            isLoading={isLoading}
            filters={searchFilters}
            onSetFilters={setSearchFilters}
            onSearchSubmit={handleSearchOrdersSubmit}
            onFilterClick={filterDrawerRoute.open}
            filterLabels={orderSearchLabels}
            valueRenderers={orderSearchRenderers}
          />

          {showPayNow && <PayNowButton hasNoPaymentMethod={hasNoPaymentMethod} onClick={openPayNow} />}

          {user.isSandbox && isCustomerOrUser && (
            <Menu width={240} position="bottom-end" withinPortal>
              <Menu.Target>
                <Button
                  variant="filled"
                  intent="primary"
                  size="large"
                  loading={isSandboxPaymentLoading}
                  aria-label={"Test Order Payments"}
                  data-testid="test-order-payments-button"
                >
                  <Group>
                    <IconV3 icon="credit_card" color="inherit" />
                    Test Order Payments
                  </Group>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: true } })}>
                  Payment Success
                </Menu.Item>
                <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: false } })}>
                  Payment Failed
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
        <OrdersTableV2
          isLoading={isLoading}
          error={error}
          orders={orders}
          withSortBy={{
            defaultValue: sortInput,
            onChange: setSortInput,
          }}
        >
          <OrdersTableFooter>
            <PaginationV2 pagination={pagination} isLoading={isLoading} />
          </OrdersTableFooter>
        </OrdersTableV2>
      </div>
      <OrdersFilter
        isOpened={filterDrawerRoute.isOpen}
        onClose={filterDrawerRoute.close}
        filters={searchFilters}
        onApply={setSearchFilters}
      />
      <PayNowDrawer opened={payNowOpen} onClose={closePayNow} />
    </MantineProvider>
  );
};
